import {
  Box,
  Button,
  TextInput,
  Grid,
  Textarea,
  Group,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { yupResolver } from "mantine-form-yup-resolver";
import { Database } from "types/supabase";
import * as yup from "yup";
import { useCreateVenue, useUpdateVenue } from "hooks/venues";
import { EditPicture, TranslateButton } from "ui";
import { useState } from "react";
import { Map, Marker } from "@vis.gl/react-google-maps";
import { isLocationFormatValid } from "utils";
import { useAppTranslation } from "hooks/useTranslation";
import { CitySelect } from "components/cities";

interface VenueFormProps {
  isNew?: boolean;
  venue?: Database["public"]["Tables"]["venues"]["Row"];
  /**
   * Embedded in the desk's Save-as-venue modal (33.3 D-06): report the created
   * venue instead of leaving the page. Without it, creating navigates to
   * /venues as before.
   */
  onCreated?: (venue: { id: number; name: string }) => void;
  /** The submit button's text; defaults to the generic Create / Save. */
  submitLabel?: string;
  /**
   * 33.3 CR-01: asked right before a create, with the name and town about to
   * be saved. A venue it returns already exists, so nothing is created and
   * `onExisting` gets it. If it throws, nothing is created either.
   */
  findExisting?: (values: {
    name: string;
    cityId: number | null;
  }) => Promise<{ id: number; name: string } | null>;
  onExisting?: (venue: { id: number; name: string }) => void;
  /**
   * 33.3 WR-08: the town must be a listed city (a free-text town is refused).
   * The desk only matches a venue by city_id, so a venue without one would
   * never be found again.
   */
  requireListedCity?: boolean;
}

export const VenueForm = (props: VenueFormProps) => {
  const {
    isNew = false,
    venue,
    onCreated,
    submitLabel,
    findExisting,
    onExisting,
    requireListedCity = false,
  } = props;
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [checkingExisting, setCheckingExisting] = useState(false);
  const { t } = useAppTranslation();

  const updateVenue = useUpdateVenue(() => navigate("/venues"));
  const createVenue = useCreateVenue(
    ({ pictureFailed, ...created }) => {
      // WR-01: the venue exists; only its picture is missing.
      if (pictureFailed) {
        notifications.show({
          color: "yellow",
          message: t("venues.picture_upload_failed"),
        });
      }
      if (onCreated) {
        onCreated(created);
      } else {
        navigate("/venues");
      }
    },
    () =>
      notifications.show({
        color: "red",
        message: t("venues.create_failed"),
      })
  );

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("errors.required"))
      .min(2, t("errors.min_length", { count: 2 })),
    location: yup
      .string()
      .required(t("errors.required"))
      .test("valid-location", t("errors.invalid_location"), (value) => {
        if (!value) return false;
        return isLocationFormatValid(value);
      }),
  });

  const form = useForm({
    initialValues: {
      name: venue?.name || "",
      address: venue?.address || "",
      city: venue?.city || "",
      city_id: venue?.city_id ?? null,
      phone: venue?.phone || "",
      website: venue?.website || "",
      description: venue?.description || "",
      description_pt: venue?.description_pt || "",
      picture: venue?.picture || "",
      location: venue?.location_lat
        ? `${venue?.location_lat},${venue?.location_long}`
        : "",
    },

    validate: (values) => {
      const errors: Record<string, React.ReactNode> = {
        ...yupResolver(schema)(values),
      };
      // City is required: either a canonical city or a free-text one
      if (values.city_id === null && (!values.city || values.city.trim() === "")) {
        errors.city = t("errors.required");
      } else if (requireListedCity && values.city_id === null) {
        errors.city = t("desk.venue_location.save_as_venue_city_required");
      }
      return errors;
    },
  });

  const formSubmit = async (values: any) => {
    const editedVenue = {
      name: values.name,
      address: values.address,
      city: values.city,
      city_id: values.city_id,
      phone: values.phone,
      website: values.website,
      description: values.description,
      description_pt: values.description_pt,
      currPicture: null,
      newPicture: file,
      location_lat: isLocationFormatValid(values.location)
        ? values.location?.split(",")[0]
        : null,
      location_long: isLocationFormatValid(values.location)
        ? values.location?.split(",")[1]
        : null,
    };

    if (isNew) {
      if (findExisting) {
        let existing: { id: number; name: string } | null;
        setCheckingExisting(true);
        try {
          existing = await findExisting({
            name: values.name,
            cityId: values.city_id,
          });
        } catch {
          // No check, no create: a duplicate is worse than a retry.
          notifications.show({
            color: "red",
            message: t("desk.venue_location.save_as_venue_check_failed"),
          });
          return;
        } finally {
          setCheckingExisting(false);
        }
        if (existing) {
          onExisting?.(existing);
          return;
        }
      }
      createVenue.mutate(editedVenue);
    } else {
      updateVenue.mutate({
        id: venue!.id,
        uid: venue!.uid,

        ...editedVenue,
        currPicture: venue!.picture,
      });
    }
    // if (error) {
    //   form.setErrors(error);
    // } else {
    //   // router.push("/events");
    // }
  };

  return (
    <Box>
      <form onSubmit={form.onSubmit(formSubmit)}>
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label={t("venues.name")}
              {...form.getInputProps("name")}
              withAsterisk
            />
          </Grid.Col>
          <Grid.Col span={6}></Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("venues.address")}
              {...form.getInputProps("address")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CitySelect
              label={t("venues.city")}
              cityId={form.values.city_id}
              customCity={form.values.city_id === null ? form.values.city : ""}
              onChange={({ cityId, cityName, customCity }) => {
                form.setFieldValue("city_id", cityId);
                // No sync trigger on venues — keep the text column populated
                // with the canonical name (or the free text for a new city)
                form.setFieldValue(
                  "city",
                  cityId !== null ? cityName || "" : customCity
                );
              }}
              error={form.errors.city}
              withAsterisk
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("venues.phone")}
              {...form.getInputProps("phone")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label={t("venues.website")}
              {...form.getInputProps("website")}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Textarea
              label={t("venues.description")}
              right="sm"
              maxRows={8}
              minRows={8}
              autosize
              {...form.getInputProps("description")}
              rightSection={
                <TranslateButton
                  originalText={form.values.description_pt}
                  tragetLang="en"
                  onTranslated={(translatedText) => {
                    form.setFieldValue("description", translatedText);
                  }}
                />
              }
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Textarea
              label={t("venues.description_pt")}
              maxRows={8}
              minRows={8}
              autosize
              {...form.getInputProps("description_pt")}
              rightSection={
                <TranslateButton
                  originalText={form.values.description}
                  tragetLang="pt"
                  onTranslated={(translatedText) => {
                    form.setFieldValue("description_pt", translatedText);
                  }}
                />
              }
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              mt="md"
              label={t("venues.location")}
              {...form.getInputProps("location")}
              w={400}
              withAsterisk
            />
            <Group mt="md" w={400} h={300}>
              <Map
                zoom={12}
                center={{
                  lat: isLocationFormatValid(form.values.location)
                    ? Number(form.values.location.split(",")[0])
                    : 37.8022298161398,
                  lng: isLocationFormatValid(form.values.location)
                    ? Number(form.values.location.split(",")[1])
                    : -8.676765624229938,
                }}
                gestureHandling={"greedy"}
                disableDefaultUI={true}
              >
                {isLocationFormatValid(form.values.location) && (
                  <Marker
                    position={{
                      lat: Number(form.values.location.split(",")[0]),
                      lng: Number(form.values.location.split(",")[1]),
                    }}
                    draggable
                    onDragEnd={(e) => {
                      form.setFieldValue(
                        "location",
                        `${e.latLng!.lat()},${e.latLng!.lng()}`
                      );
                    }}
                  />
                )}
              </Map>
            </Group>
          </Grid.Col>
          <Grid.Col span={6}>
            <EditPicture
              defaultValue={venue?.picture}
              onChange={setFile}
              height={300}
              width={300}
            />
          </Grid.Col>
        </Grid>

        <Button
          mt="md"
          type="submit"
          loading={
            updateVenue.isPending || createVenue.isPending || checkingExisting
          }
        >
          {submitLabel ?? (isNew ? t("common.create") : t("common.save"))}
        </Button>
      </form>
    </Box>
  );
};
